/**
 * On every connector in the agent editor's Connectors tab: ONE status chip
 * that says whether it is shared, with a one-line tooltip —
 *
 *   "Shared · <organization>"  follows the organization's settings; updates arrive by themselves;
 *   "Shared · customized"      edited for this agent; no updates — "Reset to the organization's settings";
 *   "This agent only"          added by hand, or from before the list.
 *
 * — plus what can be done about it: a connector the organization stopped
 * sharing carries a one-time notice (dismissed with ×) and, while the entry
 * is still on the list, "Follow the organization's settings again"; a
 * connector that needs a key the organization does not share says so and
 * takes one inline ("Enter key" is not a customization).
 *
 * A render prop, because the chip belongs in the card's pill row and the
 * notice / key form below it, and both share one state.
 */

import { useState, type ReactNode } from 'react';
import { Tooltip } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { showNotification } from '@/utils/showNotification';
import type { ApiConnector } from '@modules/chatbots/services/apiConnectorService';
import type { McpConnector } from '@modules/chatbots/services/mcpConnectorService';
import {
  connectorLibraryService,
  errorBody,
  type ConnectorLibraryStatus,
  type LibraryCredential,
  type LibraryKind,
} from '../services/connectorLibraryService';
import { useConfirmStep } from '../hooks/useConfirmStep';
import { CloseGlyph, CredentialFields, credentialComplete } from './LibraryBits';
import './ConnectorLibrary.css';

type AnyConnector = McpConnector | ApiConnector;

interface Props<T extends AnyConnector> {
  chatbotId: string;
  kind: LibraryKind;
  connector: T;
  disabled?: boolean;
  onChanged: (next: T) => void;
  children: (parts: { chips: ReactNode; panel: ReactNode }) => ReactNode;
}

/** The chip alone — the same words wherever a status is shown. */
export function StatusChip({ status, testId }: { status: ConnectorLibraryStatus | null | undefined; testId?: string }) {
  const { t } = useLanguage();
  const chip = status?.chip ?? 'own';
  const sharedWith = chip === 'shared' ? status?.organization : null;
  const text = chip === 'shared' ? t('connectorLibrary.status.sharedShort')
    : chip === 'customized' ? t('connectorLibrary.status.customized') : t('connectorLibrary.status.own');
  const tip = chip === 'shared' ? t('connectorLibrary.status.sharedTip')
    : chip === 'customized' ? t('connectorLibrary.status.customizedTip') : t('connectorLibrary.status.ownTip');
  const tone = chip === 'shared' ? 'cx-pill--accent' : chip === 'customized' ? 'cxl-pill--customized' : '';
  return (
    <Tooltip title={tip} trigger={['hover', 'focus']}>
      {/* Focusable, so the tooltip reaches keyboard users too. */}
      <span className={`cx-pill ${tone}`} tabIndex={0}
            aria-label={`${text}${sharedWith ? ` · ${sharedWith}` : ''} — ${tip}`}
            data-testid={testId} data-status={chip}>
        {/* One inline child: a flex pill drops the spaces between its children. */}
        <span>{text}{sharedWith && <> · <span dir="auto">{sharedWith}</span></>}</span>
      </span>
    </Tooltip>
  );
}

export function LibraryConnectorStatus<T extends AnyConnector>({
  chatbotId, kind, connector, disabled, onChanged, children,
}: Props<T>) {
  const { t } = useLanguage();
  const status = connector.library ?? null;
  const [busy, setBusy] = useState(false);
  // 'key': "Enter key". 'reset': the organization's settings need this
  // agent's own key before it can follow them.
  const [asking, setAsking] = useState<'key' | 'reset' | null>(null);
  const [credential, setCredential] = useState<LibraryCredential>({});
  const [error, setError] = useState<string | null>(null);
  const { confirm, modal } = useConfirmStep();

  const shape = {
    kind,
    auth_header_name: status?.auth_header_name,
    auth_type: status?.auth_type,
    auth_meta: status?.auth_meta,
  };
  const off = disabled || busy;

  const run = async (fn: () => Promise<T>, success?: string) => {
    setBusy(true);
    setError(null);
    try {
      const next = await fn();
      onChanged(next);
      setAsking(null);
      setCredential({});
      if (success) showNotification(success, 'success');
    } catch (err) {
      const body = errorBody(err);
      if (body.field === 'credential') {
        setAsking('reset');
        setError(t('connectorLibrary.connector.resetNeedsKey'));
      } else {
        setError(body.error || t('connectorLibrary.connector.actionError'));
      }
    } finally {
      setBusy(false);
    }
  };

  const reset = async (withCredential?: LibraryCredential) => {
    if (!withCredential) {
      const following = status?.chip === 'own';
      const ok = await confirm({
        title: following
          ? t('connectorLibrary.connector.followTitle', { name: connector.name })
          : t('connectorLibrary.connector.resetTitle', { name: connector.name }),
        message: following ? t('connectorLibrary.connector.followBody') : t('connectorLibrary.connector.resetBody'),
        confirmText: following ? t('connectorLibrary.connector.follow') : t('connectorLibrary.connector.reset'),
        cancelText: t('common.cancel'),
        variant: 'warning',
      });
      if (!ok) return;
    }
    await run(() => connectorLibraryService.reset<T>(chatbotId, kind, connector.connector_id, withCredential),
      t('connectorLibrary.connector.followingNow', { name: connector.name }));
  };

  const saveKey = () => (asking === 'reset'
    ? reset(credential)
    : run(() => connectorLibraryService.setKey<T>(chatbotId, kind, connector.connector_id, credential),
      t('connectorLibrary.connector.keySaved', { name: connector.name })));

  const dismiss = () => run(() => connectorLibraryService.dismissNotice<T>(chatbotId, kind, connector.connector_id));

  const chips = (
    <>
      <StatusChip status={status} testId={`connector-status-${connector.slug}`} />
      {status?.needs_key && (
        <>
          <span className="cx-pill cx-pill--error" data-testid={`connector-needs-key-${connector.slug}`}>
            {t('connectorLibrary.connector.needsKey')}
          </span>
          {asking !== 'key' && (
            <button type="button" className="cxc__link" disabled={off}
                    onClick={() => { setAsking('key'); setError(null); setCredential({}); }}
                    data-testid={`connector-enter-key-${connector.slug}`}>
              {t('connectorLibrary.connector.enterKey')}
            </button>
          )}
        </>
      )}
      {status?.available && status.chip === 'customized' && (
        <button type="button" className="cxc__link cxc__link--nowrap" disabled={off} onClick={() => { void reset(); }}
                data-testid={`connector-reset-${connector.slug}`}>
          {t('connectorLibrary.connector.reset')}
        </button>
      )}
      {status?.available && status.chip === 'own' && (
        <button type="button" className="cxc__link" disabled={off} onClick={() => { void reset(); }}
                data-testid={`connector-follow-${connector.slug}`}>
          {t('connectorLibrary.connector.follow')}
        </button>
      )}
      {modal}
    </>
  );

  const panel = (status?.notice || asking || error) ? (
    <div className="cxl-connector-panel">
      {status?.notice && (
        <div className="cxl-notice" role="status" data-testid={`connector-notice-${connector.slug}`}>
          <span className="cxl-notice__text">{t('connectorLibrary.connector.notice')}</span>
          <button type="button" className="cxl-notice__dismiss" disabled={off} onClick={() => { void dismiss(); }}
                  aria-label={t('connectorLibrary.connector.dismiss')} data-testid={`connector-dismiss-${connector.slug}`}>
            <CloseGlyph />
          </button>
        </div>
      )}
      {asking && (
        <div className="cxl-pick__credential" data-testid={`connector-key-form-${connector.slug}`}>
          <p className="cxl-pick__credential-hint">
            {asking === 'key' ? t('connectorLibrary.connector.keyHint') : t('connectorLibrary.connector.resetKeyHint')}
          </p>
          <CredentialFields entry={shape} value={credential} onChange={setCredential} />
          <div className="cxl-pick__credential-actions">
            <button type="button" className="cx-btn cx-btn--sm" disabled={busy}
                    onClick={() => { setAsking(null); setError(null); setCredential({}); }}>
              {t('common.cancel')}
            </button>
            <button type="button" className="cx-btn cx-btn--primary cx-btn--sm"
                    disabled={off || !credentialComplete(shape, credential)} onClick={() => { void saveKey(); }}
                    data-testid={`connector-key-save-${connector.slug}`}>
              {busy ? t('common.saving')
                : asking === 'reset' ? t('connectorLibrary.connector.follow') : t('connectorLibrary.connector.saveKey')}
            </button>
          </div>
        </div>
      )}
      {error && <p className="cxl-connector-panel__error" role="alert">{error}</p>}
    </div>
  ) : null;

  return <>{children({ chips, panel })}</>;
}
